/**
 * True when the drop-in is running inside an iframe. Apple Pay's merchant
 * validation only needs overriding in that case — see specs/app/index.md.
 */
export function isIframed(): boolean {
  return window.top !== window.self
}

/**
 * Bare hostnames (no scheme, no port) for every entry in `ancestorOrigins`, or
 * `null` when it is absent or empty. Engine-supplied, so none of these entries
 * can be forged by the embedding page.
 */
function ancestorHosts(): string[] | null {
  const ancestors = window.location.ancestorOrigins
  if (!ancestors || ancestors.length === 0) return null

  return Array.from(ancestors, (origin) => new URL(origin).hostname)
}

/**
 * The host to send as Apple Pay's merchant-session `domainName`, or `null` when
 * Apple Pay must stay disabled. `declaredHost` is the embedder's declaration
 * that a payment may legitimately originate from its embedding context; when
 * present it MUST appear somewhere in the browser-reported ancestor chain, and
 * an absent declaration refuses rather than falling back. The returned value
 * is always the chain's last entry — the actual top-level host Safari
 * validates against — never the declaration itself, so a declaration matching
 * an intermediate frame (e.g. an HPP nested under a merchant page) still
 * yields the merchant's own host. See specs/app/index.md::Apple Pay.
 */
export function verifiedParentHost(declaredHost?: string): string | null {
  const hosts = ancestorHosts()
  if (!hosts) return null
  if (declaredHost !== undefined && !hosts.includes(declaredHost)) return null

  return hosts[hosts.length - 1]
}
